import React, { useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { formatHourValue } from '../utils/commonUtils';

const getOrdinal = (d) => {
  const day = Number(d);
  if (!day) return '';
  if (day % 10 === 1 && day % 100 !== 11) return 'st';
  if (day % 10 === 2 && day % 100 !== 12) return 'nd';
  if (day % 10 === 3 && day % 100 !== 13) return 'rd';
  return 'th';
};

const RiskAnalysisModal = ({
  show,
  onHide,
  data = [],
  title = "",
  target = null,
  date = null,
  columns: customColumns = null,
  columnName
}) => { 
  const tableData = data.length > 0 ? data : [];

  const defaultColumns = useMemo(
    () => [
      {
        accessorKey: 'branchName',
        header: () => (
          <span className="text-navy fw-700 text-16">
            {columnName}
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-axis fw-500 text-16">
            {getValue()}
          </span>
        ),
      },
      {
        accessorKey: 'target',
        header: () => (
          <span className="text-navy fw-700 text-16">
            Target
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-axis fw-500 text-16">
            {getValue()}
          </span>
        ),
      },
      {
        accessorKey: 'result',
        header: () => (
          <span className="text-navy fw-700 text-16">
            Result
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-axis fw-500 text-16">
            {getValue()}
          </span>
        ),
      },
      {
        accessorKey: 'riskLevel',
        header: () => (
          <span className="text-navy fw-700 text-16">
            Risk Level
          </span>
        ),
        cell: ({ getValue }) => {
          const value = getValue();
          let badgeText = '';

          if (value === 'green') {
            badgeText = 'Green';
          } else if (value === 'amber' || value === 'yellow') {
            badgeText = 'Amber';
          } else {
            badgeText = 'Red';
          }

          return (
            <div className={`risk-level-badge ${value === 'green' ? 'green' : (value === 'amber' || value === 'yellow') ? 'amber' : 'red'}`}>
              {badgeText}
            </div>
          );
        },
      },
    ],
    [columnName]
  );

  const columns = customColumns || defaultColumns;

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // format date with ordinal (e.g., 13th August 2025)
  let formattedDate = date;
  // if (date) {
  //   const d = new Date(date);
  //   const day = d.getDate();
  //   const suffix = getOrdinal(day);
  //   const month = d.toLocaleString('en-GB', { month: 'long' });
  //   const year = d.getFullYear();
  //   formattedDate = `${day}${suffix} ${month} ${year}`;
  // }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="risk-analysis-modal-title"
      centered
      className="risk-modal"
    >
      <div className="risk-modal-header">
        <div className="risk-modal-header-left">
          <h5 className="risk-modal-title">{title}</h5>
        </div>

        <div className="risk-modal-header-right">
          {/* {target !== null && <div className="risk-modal-target">Target: {target}</div>} */}
          {formattedDate && (
            <div className="risk-modal-date">
              <img src="/assets/icons/calendar.svg" alt="Calendar" className="w-h-16" />
              <span>{formattedDate}</span>
            </div>
          )}
        </div>
      </div>

      <Modal.Body className="risk-modal-body">
        <div className="risk-modal-table-wrapper">
          <table className="risk-modal-table">
            <thead>
              <tr>
                {table.getHeaderGroups().map(headerGroup =>
                  headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))
                )} 
              </tr>
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, rIdx) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default RiskAnalysisModal;
